/*****************************************************************************
 * MASCA — Estado único de dirección y cobertura del storefront
 * ---------------------------------------------------------------------------
 * POR QUÉ EXISTE ESTE ARCHIVO
 * Antes había TRES memorias distintas de lo mismo, con vidas distintas:
 *   1. `userAddress` en localStorage  -> la leía el header. Dura para siempre.
 *   2. Los atributos del CARRITO      -> los lee la Shopify Function del
 *      checkout. Duran lo que dure la cookie de carrito.
 *   3. `mascaCobertura` (string) vs `masca_cobertura` (JSON) -> dos llaves
 *      distintas para el mismo dato: el popup escribía una y el gate leía otra.
 *
 * Resultado: abrías una ventana nueva, el header mostraba tu dirección (venía
 * de localStorage, que sobrevive a todo) pero el checkout te decía "ingresa tu
 * dirección" (el carrito ya no traía el atributo). Nadie las volvía a
 * sincronizar: el atributo de carrito solo se escribía al confirmar el popup.
 *
 * Este módulo es LA fuente de verdad del front. El atributo de carrito pasa a
 * ser estado DERIVADO: se reescribe en cada carga si se perdió. Así el header y
 * el checkout no pueden contradecirse.
 *
 * ADEMÁS: la cobertura CADUCA. `/coverage` depende de la hora — `express` se
 * cae cuando la petshop cierra. Una dirección confirmada anoche no es una
 * cobertura válida hoy. Pasado el TTL se vuelve a preguntar (popup precargado)
 * y se limpian los atributos del carrito, para que nadie pague con una
 * cobertura vencida.
 *****************************************************************************/
(function () {
  'use strict';

  /** Llave única en localStorage. Todo lo demás son espejos de compatibilidad. */
  var LLAVE = 'masca_estado';

  /**
   * Cuánto vale una cobertura confirmada, en milisegundos.
   * 12 h a propósito: lo que caduca de verdad es el horario de la petshop, que
   * cambia una o dos veces al día. Con 30 min castigaríamos con un popup al que
   * compra en la mañana y vuelve en la tarde.
   */
  var TTL_MS = 12 * 60 * 60 * 1000;

  /*************************************************************************
   * LECTURA / ESCRITURA
   *************************************************************************/

  /** Devuelve el estado guardado, o null si no hay o está corrupto. */
  function leer() {
    try {
      var crudo = localStorage.getItem(LLAVE);
      if (!crudo) return null;
      var est = JSON.parse(crudo);
      return est && est.direccion ? est : null;
    } catch (e) {
      return null;
    }
  }

  /** ¿La cobertura sigue vigente? Sin `confirmado_at` se considera vencida. */
  function vigente(est) {
    if (!est || !est.confirmado_at) return false;
    return Date.now() - est.confirmado_at < TTL_MS;
  }

  /**
   * Guarda el estado y actualiza los espejos legacy.
   * Los espejos NO son decorativos: `checkoutCustomer` lo lee external-cart.js
   * y `userAddress` lo leen secciones antiguas del tema. Se mantienen hasta que
   * todo el tema pase a MascaEstado.
   */
  function guardar(est) {
    est.confirmado_at = est.confirmado_at || Date.now();
    try {
      localStorage.setItem(LLAVE, JSON.stringify(est));
      // --- espejos de compatibilidad ---
      localStorage.setItem('userAddress', est.direccion || '');
      localStorage.setItem('userCity', est.comuna || '');
      localStorage.setItem('userLat', est.lat != null ? String(est.lat) : '');
      localStorage.setItem('userLng', est.lng != null ? String(est.lng) : '');
      localStorage.setItem('mascaCobertura', est.cobertura || 'no');
      localStorage.setItem('mascaRutas', (est.rutas || []).join(','));
      localStorage.setItem('mascaStore', est.store_nombre || '');
      if (est.eta_min) localStorage.setItem('mascaEta', String(est.eta_min));
      if (est.customer) {
        localStorage.setItem('checkoutCustomer', JSON.stringify(est.customer));
      }
    } catch (e) {
      /* modo incógnito con storage bloqueado: no rompemos la página */
    }
    pintar();
    try {
      window.dispatchEvent(new CustomEvent('masca:estado', { detail: est }));
    } catch (e) {}
    return est;
  }

  /** Borra el estado y sus espejos (no toca el carrito; eso es `limpiarCarrito`). */
  function borrar() {
    var llaves = [LLAVE, 'userAddress', 'userCity', 'userLat', 'userLng',
                  'mascaCobertura', 'mascaRutas', 'mascaStore', 'mascaEta',
                  'masca_cobertura'];
    try {
      llaves.forEach(function (k) { localStorage.removeItem(k); });
    } catch (e) {}
    pintar();
  }

  /*************************************************************************
   * MIGRACIÓN DE LAS LLAVES VIEJAS
   * Un comprador que ya tenía dirección guardada no debería perderla porque
   * cambiamos el formato. Se arma el estado nuevo con lo que haya y se le pone
   * `confirmado_at` viejo a propósito: la dirección se conserva (para precargar
   * el popup) pero la cobertura se considera vencida y se revalida.
   *************************************************************************/
  function migrar() {
    if (leer()) return; // ya está migrado

    var direccion = '';
    var comuna = '';
    var rutas = [];
    var cobertura = null;
    var store = '';

    // Formato viejo A: llaves sueltas que escribía popup-location.js
    try {
      direccion = localStorage.getItem('userAddress') || '';
      comuna = localStorage.getItem('userCity') || '';
      cobertura = localStorage.getItem('mascaCobertura');
      store = localStorage.getItem('mascaStore') || '';
      var r = localStorage.getItem('mascaRutas');
      if (r) rutas = r.split(',').filter(Boolean);
    } catch (e) {}

    // Formato viejo B: JSON que escribía la sección masca-cobertura-gate
    if (!direccion) {
      try {
        var viejo = JSON.parse(localStorage.getItem('masca_cobertura') || 'null');
        if (viejo && viejo.direccion) {
          direccion = viejo.direccion;
          cobertura = viejo.estado;
          rutas = viejo.rutas || [];
          store = viejo.store_nombre || '';
        }
      } catch (e) {}
    }

    if (!direccion) return; // no había nada que migrar

    var lat = parseFloat(localStorage.getItem('userLat'));
    var lng = parseFloat(localStorage.getItem('userLng'));

    guardar({
      direccion: direccion,
      comuna: comuna,
      lat: isNaN(lat) ? null : lat,
      lng: isNaN(lng) ? null : lng,
      cobertura: cobertura === 'ok' ? 'ok' : 'no',
      rutas: rutas,
      store_nombre: store,
      eta_min: null,
      // Vencido a propósito: la dirección sirve para precargar, la cobertura no.
      confirmado_at: 1,
    });
  }

  /*************************************************************************
   * ATRIBUTOS DE CARRITO (lo que lee la Shopify Function del checkout)
   *************************************************************************/

  /** Traduce el estado a los atributos que espera la Function. */
  function atributosDe(est) {
    return {
      masca_cobertura: est && est.cobertura === 'ok' ? 'ok' : 'no',
      masca_rutas: est ? (est.rutas || []).join(',') : '',
      masca_store: est ? (est.store_nombre || '') : '',
      masca_direccion: est ? (est.direccion || '') : '',
    };
  }

  /** Escribe los atributos en el carrito de Shopify. Devuelve una promesa. */
  function escribirCarrito(est) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: atributosDe(est) }),
    }).catch(function (err) {
      console.error('MASCA: no se pudo escribir el atributo de carrito', err);
    });
  }

  /**
   * Borra los atributos del carrito (Shopify borra un atributo con "").
   * Se usa cuando la cobertura venció: si quedaran los viejos, alguien con
   * productos ya en el carrito podría cerrar el popup e ir directo a pagar con
   * una cobertura de ayer.
   */
  function limpiarCarrito() {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { masca_cobertura: '', masca_rutas: '', masca_store: '', masca_direccion: '' },
      }),
    }).catch(function () {});
  }

  /**
   * EL ARREGLO CENTRAL. En cada carga de página compara el estado del navegador
   * con los atributos del carrito y los deja iguales:
   *   - vigente y el carrito perdió el atributo -> se reescribe (caso "ventana
   *     nueva": el header mostraba dirección y el checkout la pedía igual).
   *   - vencido -> se limpian, para que nadie pague con cobertura de ayer.
   * Devuelve una promesa que resuelve en el estado (o null).
   */
  function reconciliar() {
    var est = leer();

    if (!est || !vigente(est)) {
      return limpiarCarrito().then(function () { return est; });
    }

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (carrito) {
        var attrs = (carrito && carrito.attributes) || {};
        var esperado = atributosDe(est);
        var desincronizado = Object.keys(esperado).some(function (k) {
          return (attrs[k] || '') !== esperado[k];
        });
        if (desincronizado) return escribirCarrito(est);
      })
      .catch(function () {
        // Si no pudimos leer el carrito, escribimos igual: es idempotente y
        // más barato que dejar el checkout bloqueado por un atributo perdido.
        return escribirCarrito(est);
      })
      .then(function () { return est; });
  }

  /*************************************************************************
   * PINTADO DEL HEADER
   * El header ahora muestra ESTADO, no solo texto. Mostrar la dirección como si
   * nada cuando la cobertura venció o dio "no" es parte de la confusión que
   * estamos arreglando.
   *************************************************************************/

  /**
   * Etiqueta corta de la ruta disponible: "Llega hoy" / "Llega mañana".
   *
   * OJO CON EL VOCABULARIO. Los tokens son TRES —`express`, `nextday` y
   * `bodega`— y están definidos en `src/common/rutas.ts` (TokenCarrito). El
   * `petshop` de antes ya no existe: desapareció cuando "Llega mañana" dejó de
   * significar una sola cosa (puede salir de la bodega del partner o de una
   * petshop que el partner retira).
   *
   * Mientras esta función siguió buscando `petshop`, el header NUNCA pudo decir
   * "Llega hoy": con express disponible caía en "Con cobertura", o peor, decía
   * "Llega mañana" si además había bodega. Le prometía al comprador la entrega
   * lenta teniendo la rápida disponible.
   */
  function etiqueta(est) {
    if (!est) return '';
    if (!vigente(est)) return 'Confirma tu dirección';
    if (est.cobertura !== 'ok') return 'Sin cobertura';
    var rutas = est.rutas || [];
    if (rutas.indexOf('express') !== -1) return 'Llega hoy';
    // Las dos rutas de mañana se ven igual desde el header: al comprador le
    // importa CUÁNDO llega, no de qué bodega sale.
    if (rutas.indexOf('nextday') !== -1 || rutas.indexOf('bodega') !== -1) {
      return 'Llega mañana';
    }
    return 'Con cobertura';
  }

  /** Escribe dirección + estado en el header de escritorio y en el menú móvil. */
  function pintar() {
    var est = leer();
    var texto = est && est.direccion ? est.direccion : 'Seleccionar ubicación';

    var elHeader = document.getElementById('change-location-btn');
    if (elHeader) elHeader.textContent = texto;

    var elMovil = document.getElementById('mbm-address-text');
    if (elMovil) elMovil.textContent = est && est.direccion ? est.direccion : 'Seleccionar dirección';

    var badge = document.getElementById('masca-estado-badge');
    if (badge) {
      var et = etiqueta(est);
      badge.textContent = et ? '· ' + et : '';
      badge.setAttribute('data-estado', est && vigente(est) ? (est.cobertura || 'no') : 'vencido');
    }
  }

  /*************************************************************************
   * API PÚBLICA
   *************************************************************************/
  window.MascaEstado = {
    LLAVE: LLAVE,
    TTL_MS: TTL_MS,
    leer: leer,
    guardar: guardar,
    borrar: borrar,
    vigente: vigente,
    vigenteAhora: function () { return vigente(leer()); },
    atributosDe: atributosDe,
    escribirCarrito: escribirCarrito,
    limpiarCarrito: limpiarCarrito,
    reconciliar: reconciliar,
    etiqueta: etiqueta,
    pintar: pintar,
  };

  // Migrar y pintar apenas se pueda: el header no debe parpadear.
  migrar();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pintar);
  } else {
    pintar();
  }
})();
